import { faArrowLeft, faCheckCircle, faClose, faPaperPlane } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Checkbox, CircularProgress, Drawer, FormControlLabel, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { Recipient } from "../../../models";
import { useGetReceiptRecipientsByIdQuery, useSendReceiptMutation } from "../../../redux/apiStore";

export enum ReceiptDrawerView {
  PREVIEW = "PREVIEW",
  RECIPIENTS = "RECIPIENTS",
}

interface ReceiptPreviewDrawerProps {
  open: boolean;
  receiptId: number | null;
  html: string;
  isLoadingHtml: boolean;
  onClose: () => void;
}

export function ReceiptPreviewDrawer(props: ReceiptPreviewDrawerProps) {
  const { open, receiptId, html, isLoadingHtml, onClose } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();

  const [currentView, setCurrentView] = useState<ReceiptDrawerView>(ReceiptDrawerView.PREVIEW);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const recipientsQuery = useGetReceiptRecipientsByIdQuery({ id: receiptId ?? undefined }, { skip: !receiptId || currentView === ReceiptDrawerView.PREVIEW });
  const [sendReceipt] = useSendReceiptMutation();

  const recipientsInitializedRef = useRef<boolean>(false);

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (open && receiptId) {
      setCurrentView(ReceiptDrawerView.PREVIEW);
      setSelectedRecipients([]);
      setIsSending(false);
      setFadeOut(false);
      recipientsInitializedRef.current = false;
    }
  }, [open, receiptId]);

  // Pre-select owner when recipients load (only once per load)
  useEffect(() => {
    if (currentView === ReceiptDrawerView.RECIPIENTS && recipientsQuery.data && !recipientsInitializedRef.current) {
      const owner = recipientsQuery.data.find((r: Recipient) => r.isOwner);
      if (owner) {
        setSelectedRecipients([owner.email]);
      }
      recipientsInitializedRef.current = true;
    }
  }, [currentView, recipientsQuery.data]);

  const handleTransitionToView = (newView: ReceiptDrawerView) => {
    setFadeOut(true);
    setTimeout(() => {
      setCurrentView(newView);
      setFadeOut(false);
    }, 150);
  };

  const goToRecipients = () => {
    handleTransitionToView(ReceiptDrawerView.RECIPIENTS);
  };

  const goToPreview = () => {
    handleTransitionToView(ReceiptDrawerView.PREVIEW);
  };

  const handleRecipientToggle = (email: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedRecipients((prev) => [...prev, email]);
    } else {
      setSelectedRecipients((prev) => prev.filter((e) => e !== email));
    }
  };

  const handleSendReceipt = async () => {
    if (selectedRecipients.length === 0) {
      enqueueSnackbar("Please select at least one recipient", { variant: "warning" });
      return;
    }

    if (!receiptId) return;

    setIsSending(true);
    try {
      await sendReceipt({ id: receiptId, recipientEmails: selectedRecipients }).unwrap();
      enqueueSnackbar("Receipt sent successfully!", { variant: "success" });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      enqueueSnackbar("Failed to send receipt", { variant: "error" });
      console.error("Send receipt error:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: isSmallScreen ? "100%" : currentView === ReceiptDrawerView.RECIPIENTS ? "550px" : "900px",
            zIndex: 1550,
            maxHeight: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            transition: "width 500ms cubic-bezier(0.4, 0, 0.2, 1)",
          },
        },
      }}
    >
      {/* Content container */}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* PREVIEW VIEW */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: currentView === ReceiptDrawerView.PREVIEW ? (fadeOut ? 0 : 1) : 0,
            visibility: currentView === ReceiptDrawerView.PREVIEW ? "visible" : "hidden",
            transition: "opacity 150ms ease-in-out",
            pointerEvents: currentView === ReceiptDrawerView.PREVIEW ? "auto" : "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Receipt
            </Typography>
            <IconButton size="large" onClick={handleClose} sx={{ width: 44, height: 44 }}>
              <FontAwesomeIcon icon={faClose} fontSize="20px" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
            {isLoadingHtml && (
              <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center" gap={2}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading receipt preview...
                </Typography>
              </Stack>
            )}

            {!isLoadingHtml && html && (
              <iframe title="Receipt Preview" srcDoc={html} style={{ display: "block", border: 0, width: "100%", height: "100%", backgroundColor: "white" }} />
            )}

            {!isLoadingHtml && !html && (
              <Stack sx={{ height: "100%", p: 3 }} alignItems="center" justifyContent="center">
                <Typography variant="body2" color="text.secondary">
                  Unable to load receipt preview.
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              p: 2,
              display: "flex",
              flexDirection: { xs: "column-reverse", md: "row" },
              gap: 1,
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            <Button variant="outlined" onClick={handleClose} sx={{ height: 50, width: { xs: "100%", md: "auto" } }}>
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={goToRecipients}
              sx={{ height: 50, width: { xs: "100%", md: "auto" } }}
            >
              Send Receipt
            </Button>
          </Box>
        </Box>

        {/* RECIPIENTS VIEW */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: currentView === ReceiptDrawerView.RECIPIENTS ? (fadeOut ? 0 : 1) : 0,
            visibility: currentView === ReceiptDrawerView.RECIPIENTS ? "visible" : "hidden",
            transition: "opacity 150ms ease-in-out",
            pointerEvents: currentView === ReceiptDrawerView.RECIPIENTS ? "auto" : "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
            }}
          >
            <IconButton size="small" onClick={goToPreview} sx={{ width: 44, height: 44 }}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Send Receipt
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {recipientsQuery.isFetching && (
              <Stack alignItems="center" justifyContent="center" gap={2}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading recipients...
                </Typography>
              </Stack>
            )}

            {!recipientsQuery.isFetching && recipientsQuery.data && (
              <Stack gap={2}>
                <Typography variant="body2" color="text.secondary">
                  Select recipients to send the receipt to:
                </Typography>
                {recipientsQuery.data.map((recipient: Recipient) => (
                  <FormControlLabel
                    key={recipient.email}
                    control={
                      <Checkbox
                        checked={selectedRecipients.includes(recipient.email)}
                        onChange={(e) => handleRecipientToggle(recipient.email, e.target.checked)}
                        disabled={isSending}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{recipient.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {recipient.email}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Stack>
            )}

            {!recipientsQuery.isFetching && (!recipientsQuery.data || recipientsQuery.data.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No recipients available.
              </Typography>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              p: 2,
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            <Button variant="outlined" onClick={goToPreview} disabled={isSending} sx={{ height: 50 }}>
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={isSending ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faCheckCircle} />}
              onClick={handleSendReceipt}
              disabled={isSending || selectedRecipients.length === 0}
              sx={{ height: 50 }}
            >
              {isSending ? "Sending..." : "Send"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
